import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../utils/api';

const ThemeContext = createContext();

export const THEMES = {
  ios: {
    name: 'ios-2',
    id: 'ios',
    className: 'theme-ios'
  },
  standard: {
    name: 'Standard Theme',
    id: 'standard',
    className: 'theme-standard'
  },
  default: {
    name: 'ios-1',
    id: 'default',
    className: 'theme-default'
  }
};

export function ThemeProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [theme, setTheme] = useState(() => {
    // Initial state - just use localStorage as fallback
    const savedTheme = localStorage.getItem('app-theme');
    return savedTheme && THEMES[savedTheme] ? savedTheme : 'default';
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  // Load user's theme preference after auth loads
  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to load
    }

    if (user?.themePreference && THEMES[user.themePreference]) {
      // User is logged in and has a saved theme preference
      setTheme(user.themePreference);
      localStorage.setItem('app-theme', user.themePreference);
    } else if (user) {
      // User is logged in but has no saved preference, save current theme
      localStorage.setItem('app-theme', theme);
    }
    // If no user, keep using localStorage

    setHasInitialized(true);
  }, [user, authLoading]);

  useEffect(() => {
    localStorage.setItem('app-theme', theme);
    // Apply theme class to document root
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = async (themeName) => {
    if (!THEMES[themeName]) {
      return;
    }

    setTheme(themeName);
    localStorage.setItem('app-theme', themeName);

    // If user is logged in, save to backend
    if (user?.id) {
      try {
        await apiFetch('/auth/theme', {
          method: 'PATCH',
          body: JSON.stringify({ themePreference: themeName }),
        });
      } catch (error) {
        console.error('Failed to save theme preference:', error);
        // Theme still changes locally even if save fails
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
